import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Download, Loader2 } from "lucide-react";
import { shopApi, shopError } from "../lib/shop";

const BASE = process.env.REACT_APP_BACKEND_URL;

/** Asks for a short-lived signed link — the stored file is never exposed on a public URL. */
export const guideAccess = async (slug) => (await shopApi.get(`/digital/${slug}/access`)).data;

export const downloadGuide = async (slug, fileName) => {
    try {
        const { data } = await shopApi.get(`/digital/${slug}/download`, { responseType: "blob" });
        const url = URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName || `${slug}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error(shopError(e)); }
};

/** Login-only reader: the signed link expires in 15 minutes and is stamped with the buyer's details. */
export const EbookReader = ({ slug, name, fileName, onClose }) => {
    const [link, setLink] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        let alive = true;
        guideAccess(slug)
            .then((data) => { if (alive) setLink(data); })
            .catch((e) => { if (alive) setError(shopError(e)); });
        return () => { alive = false; };
    }, [slug]);

    return createPortal((
        <div className="fixed inset-0 z-[90] bg-black/90 flex flex-col" data-testid="ebook-reader">
            <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-white/10 bg-zinc-950">
                <div className="min-w-0">
                    <div className="font-display text-lg text-white truncate">{name}</div>
                    <div className="mono text-[9px] tracking-[0.16em] text-zinc-500">
                        YOUR LICENSED COPY — WATERMARKED WITH YOUR DETAILS
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => downloadGuide(slug, fileName)} data-testid="ebook-reader-download"
                        className="btn-pill btn-secondary">
                        <Download className="w-3.5 h-3.5" /> DOWNLOAD
                    </button>
                    <button onClick={onClose} data-testid="ebook-reader-close"
                        className="p-2 text-zinc-400 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>
            <div className="flex-1 min-h-0 bg-zinc-900">
                {error ? (
                    <div className="h-full flex items-center justify-center text-sm text-zinc-400 px-6 text-center"
                        data-testid="ebook-reader-error">{error}</div>
                ) : link ? (
                    <object data={`${BASE}${link.read_url}`} type="application/pdf"
                        className="w-full h-full" data-testid="ebook-reader-frame">
                        <div className="h-full flex flex-col items-center justify-center gap-4 text-center px-6">
                            <p className="text-sm text-zinc-400">
                                Your browser can&apos;t show PDFs inline.
                            </p>
                            <a href={`${BASE}${link.read_url}`} target="_blank" rel="noreferrer"
                                className="btn-pill btn-primary" data-testid="ebook-reader-open-tab">
                                OPEN IN A NEW TAB
                            </a>
                        </div>
                    </object>
                ) : (
                    <div className="h-full flex items-center justify-center text-zinc-500">
                        <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                )}
            </div>
        </div>
    ), document.body);
};

export default EbookReader;
